import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { Alert, Box, Stack, useTheme } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useAtom, useAtomValue } from "jotai";
import Head from "next/head";
import { useEffect, useState } from "react";
import ScoringCommentList from "../../components/scoring/ScoringCommentList";
import ScoringFeedbackSection from "../../components/scoring/ScoringFeedbackSection";
import ScroingNavBar from "../../components/scoring/ScoringNavBar";
import ScoringStats from "../../components/scoring/ScoringStats";
import { APP_ELEMENT_DIMENSIONS } from "../../constants";
import {
    baseAtom,
    canAccessScoringPage,
    checkRole,
    setOrgToggle,
    setUIStateIsOptionSelected,
} from "../../stores/jotai/base";
import { cohortPeriodsAtom } from "../../stores/jotai/cohortPeriods";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { scoringSelectedPeriodAtom } from "../../stores/jotai/scoring/selectedPeriodIndex";
import { showAllCommentsAtom } from "../../stores/jotai/scoring/showAllComments";
import { useGetComments } from "../../tanstackQuery/queries/comments";
import {
    getCurrentComment,
    getScoringCommentsByPeriod
} from "../../util/scoringUtils";

const Scoring = () => {
    const [cohortPeriodsFromAtom, setCohortPeriods] = useAtom(cohortPeriodsAtom);
    const [ratingQuestionsData, setRatingQuestions] = useState([]);
    const selectedPeriodIndex = useAtomValue(scoringSelectedPeriodAtom);
    const [base, setBase] = useAtom(baseAtom);
    const muiTheme = useTheme();
    const hasAccessToScoringPage = canAccessScoringPage(base);
    const { keycloak, initialized } = useKeycloak()
    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
    const showAllComments = useAtomValue(showAllCommentsAtom);
    
    const { data: comments } = useGetComments(
        { accessToken: keycloak?.token },
        { groupID: base?.team?.groupID, sessionID: discoverySession?.sessionID },
        { enabled: !!(base?.team?.groupID && discoverySession?.sessionID) }
    );
    
    if (!hasAccessToScoringPage) {
        return (
            <Stack sx={{padding: "16px"}}>
                <Alert sx={{border: `1px solid ${muiTheme?.palette?.error?.main}`}} color="error" icon={<ReportProblemOutlinedIcon />}>
                    You're not authorized to access this page.
                </Alert>
            </Stack>
        );
    }

    const teamID = base?.team?.id;
    const periods = base?.cohort?.periods;
    const ratingQuestions = base?.cohort?.ratings || "";
    const userID = base?.user?.userID;
    const isInstructor = checkRole(base, "isInstructor");
    const currentComment = getCurrentComment(
        hasAccessToScoringPage,
        teamID,
        comments,
        selectedPeriodIndex,
        userID
    );

    const filteredComments = getScoringCommentsByPeriod({
        hasAccessToScoringPage,
        teamID,
        cohortComments: comments,
        selectedPeriodIndex: showAllComments ? undefined : selectedPeriodIndex,
        excludeCommentIDs: isInstructor ? [currentComment?.commentID] : [],
    });

    useEffect(() => {
        setCohortPeriods(periods || []);
    }, [base?.cohort?.periods]);

    useEffect(() => {
        setRatingQuestions(
            ratingQuestions.split("\n")?.filter((question) => !!question)
        );
    }, [ratingQuestions]);

    useEffect(() => {
        setUIStateIsOptionSelected(setBase, base, "chat", true);
        setOrgToggle(setBase, true);
    }, []);

    return (
        <>
            <Head>
                <title>Scoring - Innovation Within</title>
            </Head>
            <Stack
                sx={{
                    height: "100%",
                }}
            >
                <ScroingNavBar />
                <Stack
                    direction="row"
                    sx={{
                        padding: "16px",
                        overflowY: "auto",
                        height: `calc(100% - ${APP_ELEMENT_DIMENSIONS.CANVAS_TOOLBAR.HEIGHT})`,
                        flexWrap: "wrap",
                        alignContent: "flex-start",
                        gap: "16px",
                        "> .MuiStack-root": {
                            width: "100%",
                        },
                    }}
                >
                    {comments && <ScoringStats comments={comments} />}
                    {isInstructor && (
                        <ScoringFeedbackSection
                            comment={currentComment}
                            ratingQuestions={ratingQuestionsData}
                        />
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                        <ScoringCommentList
                            comments={filteredComments}
                            ratingQuestions={ratingQuestionsData}
                        />
                    </Box>
                </Stack>
            </Stack>
        </>
    );
};

export default Scoring;
